import {AsyncPipe} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {dateToString, EmTranslatePipe, stringToDate, validators} from '@emma-labs/shared/angular';
import {
  BOOLEAN_ANSWER_SELECT_BUTTONS,
  calculateAgeTruncated,
  CategoryCode,
  convertJobCategoriesToDropdownOptions,
  ENVIRONMENT,
  EnvironmentBroker,
  getLifeInsuranceProvider,
  HUMANIA_EMMA_JOB_CATEGORY_ID_TO_RISK_CLASS,
  HUMANIA_EMMA_MIN_AGE,
  HUMANIA_EMMA_V2_ELIGIBILITY_MAX_AGE,
  JOB_CATEGORY_ID_TO_DESCRIPTION,
  JobRiskClass,
  ProviderId,
  randomDate,
  Sex,
  SEX_OPTIONS,
  SURCHARGE_OPTIONS,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NgxMaskDirective} from 'ngx-mask';
import {Button} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {TooltipModule} from 'primeng/tooltip';
import {filter, Observable, tap} from 'rxjs';
import {map} from 'rxjs/operators';
import {MainComponent} from '../main/main.component';
import {ProtectionBuilderComponent} from '../protection-builder/protection-builder.component';
import {TextIndicatorComponent} from '../text-indicator/text-indicator.component';

interface QuickQuoteForm {
  birthday: FormControl<string | null>;
  sex: FormControl<Sex | null>;
  smoker: FormControl<boolean | null>;
  surchargeRate: FormControl<number | null>;
  jobCategoryId: FormControl<number | null>;
}

@UntilDestroy()
@Component({
  selector: 'em-quick-quote-page',
  templateUrl: './quick-quote-page.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CardModule,
    Button,
    NgxMaskDirective,
    DropdownModule,
    MainComponent,
    InputTextModule,
    ProtectionBuilderComponent,
    TextIndicatorComponent,
    AsyncPipe,
    TooltipModule,
    EmTranslatePipe,
  ],
})
export class QuickQuotePageComponent {
  form: FormGroup<QuickQuoteForm>;
  age?: number;
  protectionBuilderInputs$!: Observable<{
    age: number;
    sex: Sex;
    smoker: boolean;
    surchargeRate?: number;
    jobRiskClass?: JobRiskClass;
  }>;

  readonly categoryCode = CategoryCode.C1;
  readonly ratesVersion = getLifeInsuranceProvider(ProviderId.HUMANIA_EMMA_V2).latestVersion.ratesVersion;
  readonly sexOptions = SEX_OPTIONS;
  readonly smokerOptions = BOOLEAN_ANSWER_SELECT_BUTTONS;
  readonly surchargeOptions = SURCHARGE_OPTIONS;
  readonly jobCategoryOptions = convertJobCategoriesToDropdownOptions();
  readonly jobCategoryIdToRiskClass = HUMANIA_EMMA_JOB_CATEGORY_ID_TO_RISK_CLASS;
  readonly dev: boolean;

  constructor(
    private readonly fb: FormBuilder,
    @Inject(ENVIRONMENT) readonly environment: EnvironmentBroker,
  ) {
    this.dev = environment.showDevControls;
    this.form = this.createForm();
  }

  simulateForm(): void {
    this.form.patchValue({
      birthday: dateToString(
        randomDate(
          new Date(new Date().setFullYear(new Date().getFullYear() - HUMANIA_EMMA_V2_ELIGIBILITY_MAX_AGE)),
          new Date(new Date().setFullYear(new Date().getFullYear() - HUMANIA_EMMA_MIN_AGE)),
        ),
      ),
      sex: Math.random() < 0.5 ? Sex.FEMALE : Sex.MALE,
      smoker: Math.random() < 0.5,
      surchargeRate: 0,
      jobCategoryId: Math.floor(Math.random() * Object.keys(JOB_CATEGORY_ID_TO_DESCRIPTION).length) + 1,
    });
  }

  private createForm(): FormGroup<QuickQuoteForm> {
    const form = this.fb.group<QuickQuoteForm>({
      birthday: this.fb.control(null, [
        validators.date,
        Validators.required,
        validators.dateInFuture,
        validators.atLeast18YearsOld,
      ]),
      sex: this.fb.control(null, [Validators.required]),
      smoker: this.fb.control(null, [Validators.required]),
      surchargeRate: this.fb.control(null),
      jobCategoryId: this.fb.control(null),
    });

    this.protectionBuilderInputs$ = form.valueChanges.pipe(
      tap((value) => {
        if (form.controls.birthday.valid) {
          this.age = calculateAgeTruncated(stringToDate(value.birthday!));
        } else {
          this.age = undefined;
        }
      }),
      filter(() => form.valid),
      map((value) => ({
        age: this.age!,
        sex: value.sex!,
        smoker: value.smoker!,
        surchargeRate: value.surchargeRate ?? undefined,
        jobRiskClass: value.jobCategoryId ? this.jobCategoryIdToRiskClass[value.jobCategoryId] : undefined,
      })),
      untilDestroyed(this),
    );

    return form;
  }
}
